import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, Smartphone } from 'lucide-react';

// Dummy serial for UI development
const dummySerial = 'DEMO123456';

// Dummy data for UI development
const dummyCategories = [
  { name: 'Social Media', status: 'blocked', source: 'level', tags: ['Facebook', 'Instagram', 'TikTok'] },
  { name: 'Games', status: 'allowed_with_rules', source: 'device', tags: ['Puzzle', 'Educational'] },
  { name: 'Education', status: 'allowed', source: 'level', tags: ['Learning', 'Reference'] },
  { name: 'Entertainment', status: 'blocked', source: 'level', tags: ['Streaming', 'Videos'] },
  { name: 'Productivity', status: 'allowed', source: 'device', tags: ['Notes', 'Calendar'] },
  { name: 'Communication', status: 'allowed_with_rules', source: 'level', tags: ['Messaging', 'Email'] },
  { name: 'News', status: 'allowed', source: 'device', tags: ['Articles', 'Updates'] },
  { name: 'Shopping', status: 'blocked', source: 'level', tags: ['E-commerce', 'Deals'] },
];

const Apps: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 pb-28">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-background/70 backdrop-blur-2xl border-b border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.08)]">
        <div className="px-5 pt-5 pb-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(`/${dummySerial}/settings`)}
              className="w-10 h-10 rounded-xl bg-background/60 backdrop-blur-xl border border-white/15 flex items-center justify-center hover:bg-background/80 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-foreground" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center shadow-md">
                <Smartphone className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-foreground">Categories</h1>
            </div>
          </div>
          <p className="text-[13px] text-muted-foreground mt-3 ml-[52px]">Manage allowed and blocked app categories</p>
        </div>
      </header>

      {/* Main Content */}
      <div className="px-4 pt-6">
        <div className="bg-background/60 backdrop-blur-xl rounded-2xl border border-white/15 shadow-[0_4px_16px_rgba(0,0,0,0.08),inset_0_1px_1px_rgba(255,255,255,0.1)] overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border/50">
                <TableHead className="text-[13px] font-semibold">Category</TableHead>
                <TableHead className="text-[13px] font-semibold">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dummyCategories.map((category) => {
                const { name, status, source, tags } = category;
                
                let rowBgClass = '';
                let statusColorClass = '';
                let statusText = '';
                
                if (status === 'blocked') {
                  rowBgClass = 'bg-red-50/50 hover:bg-red-50 dark:bg-red-950/10 dark:hover:bg-red-950/20';
                  statusColorClass = 'text-red-600 dark:text-red-400';
                  statusText = 'Blocked';
                } else if (status === 'allowed') {
                  rowBgClass = 'bg-green-50/50 hover:bg-green-50 dark:bg-green-950/10 dark:hover:bg-green-950/20';
                  statusColorClass = 'text-green-600 dark:text-green-400';
                  statusText = 'Allowed';
                } else if (status === 'allowed_with_rules') {
                  rowBgClass = 'bg-blue-50/50 hover:bg-blue-50 dark:bg-blue-950/10 dark:hover:bg-blue-950/20';
                  statusColorClass = 'text-blue-600 dark:text-blue-400';
                  statusText = 'With Rules';
                }
                
                return (
                  <TableRow 
                    key={name} 
                    className={`${rowBgClass} border-border/50`}
                  >
                    <TableCell className="py-3.5">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-[14px] text-foreground">{name}</span>
                          {source && (
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                              {source === 'device' ? 'Device' : 'Level'}
                            </Badge>
                          )}
                        </div>
                        {tags && tags.length > 0 && (
                          <p className="text-[12px] text-muted-foreground mt-0.5">{tags.join(', ')}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-3.5">
                      <span className={`font-semibold text-[13px] ${statusColorClass}`}>
                        {statusText}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default Apps;
